import React from 'react';
import {
  VStack,
  Text,
  Progress,
  ProgressFilledTrack,
  View,
  HStack,
  Button,
  ButtonText,
  ButtonSpinner,
  ScrollView,
  ButtonIcon,
} from '@gluestack-ui/themed';
import { useFormContext } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { FileDown } from 'lucide-react-native';
import { DataTable } from '../../../common';
import { getObjectActions } from '../../../common/ObjectActionsConfig';
import { useDialog } from '../../../../common/context/DialogContext';
import GenericDataProvider from '../../../../common/api/GenericDataProvider';
import { generatePDF } from './utils/pdfReportGenerator';
import log from '../../../../common/utils/logger';

const UserModulesReport = () => {
  const [modulesData, setModulesData] = React.useState({
    pagination: {},
    data: [],
  });
  const previousDataRef = React.useRef(null);
  const [creatingPDF, setCreatingPDF] = React.useState(false);
  const { getValues } = useFormContext();
  const {
    showDialog, hideDialog, updateDialogProps, showToastMessage,
  } = useDialog();
  const queryClient = useQueryClient();
  const [refreshKey, setRefreshKey] = React.useState(Date.now());
  const [isActionRunning, setIsActionRunning] = React.useState(false);

  const userData = getValues();
  const userId = userData.id;
  const objectName = `reports/users/${userId}/modules`;

  const rowActions = React.useMemo(
    () => getObjectActions('reports/users/[0-9]+/modules'),
    [],
  );

  const userName = React.useMemo(() => `${userData.firstname} ${userData.lastname}`, [userData]);

  const {
    numModules, numCourses, finishedModules, pendingModules, progressPercent,
  } = React.useMemo(() => {
    const totalModules = modulesData?.pagination?.total || 0;

    const modulesList = Array.isArray(modulesData?.data) ? modulesData.data : [];

    const totalCourses = modulesList.reduce((uniqueCourseIds, module) => {
      if (!uniqueCourseIds.includes(module?.course?.id)) {
        uniqueCourseIds.push(module?.course?.id);
      }
      return uniqueCourseIds;
    }, []).length;

    const gradedModules = modulesList.filter((module) => module?.correctionDate !== null).length;

    const awaitingGrading = modulesList.filter(
      (module) => module?.state === 'processing' || module?.state === 'submitted',
    ).length;

    const completedModules = gradedModules + awaitingGrading;
    const progress = totalModules > 0 ? Math.round((completedModules / totalModules) * 100) : 0;

    return {
      numModules: totalModules,
      numCourses: totalCourses,
      gradedModules,
      finishedModules: completedModules,
      pendingModules: awaitingGrading,
      progressPercent: progress,
    };
  }, [modulesData]);

  const handleGeneratePDF = React.useCallback(async () => {
    try {
      await generatePDF({
        userName,
        numCourses,
        numModules,
        finishedModules,
        pendingModules,
        progressPercent,
        modulesData,
        setCreatingPDF,
        showToastMessage,
      });
    } catch (error) {
      log.error('PDF generation failed:', error);
      showToastMessage('error', error.message || 'Failed to generate PDF');
      setCreatingPDF(false);
    }
  }, [
    userName,
    numCourses,
    numModules,
    finishedModules,
    pendingModules,
    progressPercent,
    modulesData,
    showToastMessage,
  ]);

  const handleDataCallback = React.useCallback((newData) => {
    // Only update if data has actually changed
    const currentDataString = JSON.stringify(newData);
    if (previousDataRef.current !== currentDataString) {
      previousDataRef.current = currentDataString;
      setModulesData(newData);
    }
  }, []);

  const handleTableAction = React.useCallback(async (actionCode, rowData) => {
    if (isActionRunning) return;

    const actionConfig = rowActions[actionCode];
    if (!actionConfig?.handler) {
      log.warn(`No handler found for action: ${actionCode}`);
      return;
    }

    try {
      setIsActionRunning(true);
      await actionConfig.handler({
        data: rowData,
        objectName,
        mutations: {},
        utils: {
          showDialog,
          hideDialog,
          updateDialogProps,
          showToastMessage,
          setRefreshKey,
          queryClient,
          dataProvider: new GenericDataProvider(),
          setActionRunning: setIsActionRunning,
        },
      });
    } catch (error) {
      log.error(`Error executing action ${actionCode}:`, error);
      showToastMessage('error', `Failed to execute ${actionCode}: ${error.message}`);
    } finally {
      setIsActionRunning(false);
    }
  }, [
    isActionRunning,
    rowActions,
    objectName,
    showDialog,
    hideDialog,
    updateDialogProps,
    showToastMessage,
    queryClient,
  ]);

  if (!userId) {
    return null;
  }

  return (
    <ScrollView flex={1} bg="$backgroundLight0">
      <VStack space="lg" bg="$backgroundLight0" px="$1" py="$4">
        {numModules > 0 && (
          <HStack>
            <VStack style={{ flex: 1 }}>
              <Text size="xs" color="$textSubtle" mb="$2">
                Enrolled in
                {' '}
                {numCourses}
                {' '}
                courses with
                {' '}
                {numModules}
                {' '}
                modules total.
                {' '}
                {finishedModules}
                {' '}
                module(s) completed
                (
                {progressPercent}
                %),
                {' '}
                {pendingModules}
                {' '}
                awaiting grading.
              </Text>
              <Progress value={progressPercent} size="sm">
                <ProgressFilledTrack />
              </Progress>
            </VStack>
            <View style={{ marginLeft: 24 }}>
              <Button
                size="xs"
                variant="outline"
                onPress={handleGeneratePDF}
                isDisabled={creatingPDF}
                borderColor="$borderLight300"
              >
                {creatingPDF ? (
                  <ButtonSpinner size="small" />
                ) : (
                  <>
                    <ButtonIcon as={FileDown} size="xs" />
                    <ButtonText ml="$2" size="xs" color="$textSubtle">
                      Export PDF
                    </ButtonText>
                  </>
                )}
              </Button>
            </View>
          </HStack>
        )}

        <DataTable
          dataConfig={{
            objectName,
            refreshKey,
            dataCallback: handleDataCallback,
          }}
          displayConfig={{
            noTitle: true,
            noPadding: true,
            columns: {
              'course.title': { label: 'Course', width: 350, type: 'string' },
              'module.title': { label: 'Module', width: 400, type: 'string' },
              correctionDate: { label: 'Correction Date', width: 90, type: 'date' },
              correctionDuedate: {
                label: 'Correction Due',
                width: 90,
                type: 'date',
              },
              'corrector.lastname': {
                label: 'Corrector',
                width: 100,
                type: 'string',
              },
              'grading.grade': { label: 'Grade', width: 50, type: 'string' },
              state: { label: 'State', width: 100, type: 'string' },
            },
          }}
          actionConfig={{
            actions: rowActions,
            onAction: handleTableAction,
            isActionRunning,
          }}
          searchFilterConfig={{
            showSearch: false,
          }}
        />
      </VStack>
    </ScrollView>
  );
};

export default UserModulesReport;
